import React, { useEffect, useState, useCallback } from 'react';
import { usePage } from '../../hooks/usePage';
import {
  notificationsApi,
  BroadcastNotification,
  BroadcastAnalytics,
} from '../../api/notifications';
import { Card } from '../../components/ui/Card';
import { StatCard } from '../../components/ui/StatCard';
import { Table } from '../../components/ui/Table';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { Pagination } from '../../components/ui/Pagination';
import { Spinner } from '../../components/ui/Spinner';
import { useToast } from '../../hooks/useToast';
import {
  Bell,
  Mail,
  Send,
  Users,
  Store,
  Globe,
  CheckCircle,
  XCircle,
} from 'lucide-react';

type Tab = 'analytics' | 'history' | 'compose';

const statusColors: Record<string, 'success' | 'danger' | 'warning' | 'info' | 'neutral'> = {
  pending: 'warning',
  sending: 'info',
  completed: 'success',
  failed: 'danger',
};

const targetLabels: Record<string, string> = {
  users: 'App Users',
  vendors: 'Vendors',
  all: 'All',
};

const formatDate = (iso: string | null) => {
  if (!iso) return '--';
  return new Date(iso).toLocaleDateString('en-NG', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const Notifications: React.FC = () => {
  usePage('Notifications');

  const toast = useToast();
  const [tab, setTab] = useState<Tab>('analytics');
  const [analytics, setAnalytics] = useState<BroadcastAnalytics | null>(null);
  const [broadcasts, setBroadcasts] = useState<BroadcastNotification[]>([]);
  const [meta, setMeta] = useState<{ total: number; page: number; totalPages: number } | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [composeOpen, setComposeOpen] = useState(false);

  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      const data = await notificationsApi.getAnalytics();
      setAnalytics(data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchBroadcasts = useCallback(async () => {
    try {
      setLoading(true);
      const data = await notificationsApi.listBroadcasts({ page, limit: 20 });
      setBroadcasts(data.items);
      setMeta(data.meta);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    if (tab === 'analytics') fetchAnalytics();
    else if (tab === 'history') fetchBroadcasts();
  }, [tab, fetchAnalytics, fetchBroadcasts]);

  const handleSent = () => {
    setComposeOpen(false);
    if (tab === 'analytics') fetchAnalytics();
    else if (tab === 'history') fetchBroadcasts();
  };

  const tabStyle = (t: Tab): React.CSSProperties => ({
    padding: '10px 20px',
    fontSize: 14,
    fontWeight: 600,
    color: tab === t ? 'var(--blue-700)' : 'var(--neutral-600)',
    borderBottom: tab === t ? '2px solid var(--blue-600)' : '2px solid transparent',
    cursor: 'pointer',
    background: 'none',
    transition: 'all 150ms ease',
  });

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div style={{ display: 'flex', gap: 0 }}>
          <button style={tabStyle('analytics')} onClick={() => setTab('analytics')}>Analytics</button>
          <button style={tabStyle('history')} onClick={() => setTab('history')}>History</button>
        </div>
        <Button onClick={() => setComposeOpen(true)}>
          <Send size={16} />
          New Broadcast
        </Button>
      </div>

      {tab === 'analytics' && (
        loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
            <Spinner size={28} />
          </div>
        ) : analytics ? (
          <AnalyticsView analytics={analytics} />
        ) : null
      )}

      {tab === 'history' && (
        loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
            <Spinner size={28} />
          </div>
        ) : (
          <Card padding={0}>
            <Table
              columns={[
                {
                  key: 'title',
                  header: 'Title',
                  render: (r: BroadcastNotification) => (
                    <span style={{ fontWeight: 600 }}>{r.title}</span>
                  ),
                  width: '30%',
                },
                {
                  key: 'target',
                  header: 'Target',
                  render: (r: BroadcastNotification) => (
                    <Badge variant="info">{targetLabels[r.target]}</Badge>
                  ),
                },
                {
                  key: 'channels',
                  header: 'Channels',
                  render: (r: BroadcastNotification) => (
                    <div style={{ display: 'flex', gap: 4 }}>
                      {r.channels.includes('push') && <Badge variant="neutral">Push</Badge>}
                      {r.channels.includes('email') && <Badge variant="neutral">Email</Badge>}
                    </div>
                  ),
                },
                {
                  key: 'recipients',
                  header: 'Recipients',
                  render: (r: BroadcastNotification) => r.totalRecipients.toLocaleString(),
                },
                {
                  key: 'status',
                  header: 'Status',
                  render: (r: BroadcastNotification) => (
                    <Badge variant={statusColors[r.status]}>{r.status}</Badge>
                  ),
                },
                {
                  key: 'sentBy',
                  header: 'Sent By',
                  render: (r: BroadcastNotification) =>
                    r.sentBy ? `${r.sentBy.firstName} ${r.sentBy.lastName}` : '--',
                },
                {
                  key: 'createdAt',
                  header: 'Date',
                  render: (r: BroadcastNotification) => formatDate(r.createdAt),
                },
              ]}
              data={broadcasts}
              emptyMessage="No broadcasts sent yet"
            />
            {meta && meta.totalPages > 1 && (
              <div style={{ padding: 16 }}>
                <Pagination
                  page={meta.page}
                  totalPages={meta.totalPages}
                  total={0}
                  onPageChange={setPage}
                />
              </div>
            )}
          </Card>
        )
      )}

      <ComposeModal
        isOpen={composeOpen}
        onClose={() => setComposeOpen(false)}
        onSent={handleSent}
        toast={toast}
      />
    </>
  );
};

const AnalyticsView: React.FC<{ analytics: BroadcastAnalytics }> = ({ analytics }) => {
  const pushTotal = analytics.totalPushSent + analytics.totalPushFailed;
  const emailTotal = analytics.totalEmailSent + analytics.totalEmailFailed;
  const pushRate = pushTotal > 0 ? Math.round((analytics.totalPushSent / pushTotal) * 100) : 0;
  const emailRate = emailTotal > 0 ? Math.round((analytics.totalEmailSent / emailTotal) * 100) : 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 180px), 1fr))', gap: 16 }}>
        <StatCard label="Total Broadcasts" value={analytics.totalBroadcasts} icon={Send} accent="blue" />
        <StatCard label="Total Recipients" value={analytics.totalRecipients.toLocaleString()} icon={Users} accent="green" />
        <StatCard label="Push Delivery Rate" value={`${pushRate}%`} icon={Bell} accent="purple" />
        <StatCard label="Email Delivery Rate" value={`${emailRate}%`} icon={Mail} accent="blue" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))', gap: 16 }}>
        <Card>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--neutral-800)', marginBottom: 20 }}>
            Delivery Breakdown
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <DeliveryRow
              label="Push Sent"
              value={analytics.totalPushSent}
              icon={<CheckCircle size={16} color="var(--green-700)" />}
            />
            <DeliveryRow
              label="Push Failed"
              value={analytics.totalPushFailed}
              icon={<XCircle size={16} color="var(--danger-600)" />}
            />
            <DeliveryRow
              label="Email Sent"
              value={analytics.totalEmailSent}
              icon={<CheckCircle size={16} color="var(--green-700)" />}
            />
            <DeliveryRow
              label="Email Failed"
              value={analytics.totalEmailFailed}
              icon={<XCircle size={16} color="var(--danger-600)" />}
            />
          </div>
        </Card>

        <Card>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--neutral-800)', marginBottom: 20 }}>
            Broadcasts by Target
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {analytics.byTarget.length === 0 ? (
              <span style={{ color: 'var(--neutral-500)', fontSize: 14 }}>No data yet</span>
            ) : (
              analytics.byTarget.map((t) => (
                <div key={t.target} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {t.target === 'users' && <Users size={16} color="var(--blue-600)" />}
                    {t.target === 'vendors' && <Store size={16} color="var(--purple-600)" />}
                    {t.target === 'all' && <Globe size={16} color="var(--green-700)" />}
                    <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--neutral-700)' }}>
                      {targetLabels[t.target] || t.target}
                    </span>
                  </div>
                  <span style={{ fontSize: 20, fontWeight: 700, color: 'var(--neutral-800)' }}>
                    {Number(t.count).toLocaleString()}
                  </span>
                </div>
              ))
            )}
          </div>
        </Card>
      </div>

      {analytics.recentBroadcasts.length > 0 && (
        <Card padding={0}>
          <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--neutral-300)' }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--neutral-800)' }}>
              Recent Broadcasts
            </h3>
          </div>
          <Table
            columns={[
              {
                key: 'title',
                header: 'Title',
                render: (r: BroadcastNotification) => (
                  <span style={{ fontWeight: 600 }}>{r.title}</span>
                ),
                width: '35%',
              },
              {
                key: 'target',
                header: 'Target',
                render: (r: BroadcastNotification) => (
                  <Badge variant="info">{targetLabels[r.target]}</Badge>
                ),
              },
              {
                key: 'recipients',
                header: 'Recipients',
                render: (r: BroadcastNotification) => r.totalRecipients.toLocaleString(),
              },
              {
                key: 'status',
                header: 'Status',
                render: (r: BroadcastNotification) => (
                  <Badge variant={statusColors[r.status]}>{r.status}</Badge>
                ),
              },
              {
                key: 'createdAt',
                header: 'Date',
                render: (r: BroadcastNotification) => formatDate(r.createdAt),
              },
            ]}
            data={analytics.recentBroadcasts}
          />
        </Card>
      )}
    </div>
  );
};

const DeliveryRow: React.FC<{ label: string; value: number; icon: React.ReactNode }> = ({
  label,
  value,
  icon,
}) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      {icon}
      <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--neutral-700)' }}>{label}</span>
    </div>
    <span style={{ fontSize: 20, fontWeight: 700, color: 'var(--neutral-800)' }}>
      {value.toLocaleString()}
    </span>
  </div>
);

const ComposeModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onSent: () => void;
  toast: { success: (msg: string) => void; error: (err: unknown, fallback?: string) => void };
}> = ({ isOpen, onClose, onSent, toast }) => {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [target, setTarget] = useState<'users' | 'vendors' | 'all'>('users');
  const [channels, setChannels] = useState<Set<'push' | 'email'>>(new Set<'push' | 'email'>(['push']));
  const [sending, setSending] = useState(false);

  const toggleChannel = (ch: 'push' | 'email') => {
    setChannels((prev) => {
      const next = new Set<'push' | 'email'>(prev);
      if (next.has(ch)) next.delete(ch);
      else next.add(ch);
      return next;
    });
  };

  const canSend = title.trim() && body.trim() && channels.size > 0;

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      await notificationsApi.sendBroadcast({
        title: title.trim(),
        body: body.trim(),
        target,
        channels: Array.from(channels),
      });
      toast.success('Broadcast sent successfully');
      setTitle('');
      setBody('');
      setTarget('users');
      setChannels(new Set<'push' | 'email'>(['push']));
      onSent();
    } catch (err) {
      toast.error(err, 'Failed to send broadcast');
    } finally {
      setSending(false);
    }
  };

  const selectStyle: React.CSSProperties = {
    width: '100%',
    height: 40,
    padding: '10px 14px',
    fontSize: 14,
    fontFamily: 'var(--font-family)',
    color: 'var(--neutral-800)',
    background: 'var(--neutral-100)',
    border: '1px solid var(--neutral-300)',
    borderRadius: 'var(--radius-md)',
    outline: 'none',
  };

  const chipStyle = (active: boolean): React.CSSProperties => ({
    padding: '8px 16px',
    fontSize: 13,
    fontWeight: 600,
    borderRadius: 'var(--radius-full)',
    cursor: 'pointer',
    border: active ? '2px solid var(--blue-600)' : '2px solid var(--neutral-300)',
    background: active ? 'var(--blue-100)' : 'var(--neutral-100)',
    color: active ? 'var(--blue-700)' : 'var(--neutral-600)',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    transition: 'all 150ms ease',
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Send Broadcast Notification" maxWidth={560}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <Input
          label="Title"
          placeholder="Notification title..."
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={200}
        />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--neutral-700)' }}>
            Message
          </label>
          <textarea
            placeholder="Write your message..."
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={5000}
            rows={5}
            style={{
              ...selectStyle,
              height: 'auto',
              resize: 'vertical',
              minHeight: 100,
            }}
          />
          <span style={{ fontSize: 12, color: 'var(--neutral-500)', textAlign: 'right' }}>
            {body.length}/5000
          </span>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--neutral-700)' }}>
            Target Audience
          </label>
          <select
            value={target}
            onChange={(e) => setTarget(e.target.value as 'users' | 'vendors' | 'all')}
            style={selectStyle}
          >
            <option value="users">App Users</option>
            <option value="vendors">Vendors</option>
            <option value="all">All (Users + Vendors)</option>
          </select>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--neutral-700)' }}>
            Channels
          </label>
          <div style={{ display: 'flex', gap: 10 }}>
            <button style={chipStyle(channels.has('push'))} onClick={() => toggleChannel('push')}>
              <Bell size={14} />
              Push Notification
            </button>
            <button style={chipStyle(channels.has('email'))} onClick={() => toggleChannel('email')}>
              <Mail size={14} />
              Email
            </button>
          </div>
          {channels.size === 0 && (
            <span style={{ fontSize: 12, color: 'var(--danger-600)' }}>
              Select at least one channel
            </span>
          )}
        </div>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 8 }}>
          <Button variant="secondary" onClick={onClose} disabled={sending}>
            Cancel
          </Button>
          <Button onClick={handleSend} disabled={!canSend} loading={sending}>
            <Send size={16} />
            Send Broadcast
          </Button>
        </div>
      </div>
    </Modal>
  );
};
